import {useDisclosure} from '@mantine/hooks';
import {useQueryClient} from '@tanstack/react-query';
import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {useAuth} from '../../../core/usecases/auth/AuthContext';
import {NoLoginAlertModal} from '../../../shared/components/NoLoginAlertModal';
import {IS_MAINTENANCE_MODE} from '../constants/maintenanceMode';

type RequireAuthContextType = {
  /**
   * ログイン状態をチェックし、未ログイン時はモーダルを表示する
   * @returns ログイン済みならtrue、未ログインならfalse（モーダル表示後）
   */
  requireAuth: () => boolean;
  /**
   * ログインが必要なアクションをラップする
   * 未ログイン時はアクションを実行せずモーダルを表示
   */
  withRequireAuth: <T extends (...args: any[]) => any>(
    action: T,
  ) => (...args: Parameters<T>) => ReturnType<T> | undefined;
  /**
   * ログイン済みかどうか
   */
  isAuthenticated: boolean;
  /**
   * 認証状態が確定したかどうか（initializing/loading以外）
   */
  isAuthResolved: boolean;
  /**
   * メンテナンスモードかどうか
   */
  isMaintenanceMode: boolean;
};

const RequireAuthContext = createContext<RequireAuthContextType | null>(null);

/**
 * 認証が必要なアクションを管理するProvider
 * EntryManagementLayout内で使用し、NoLoginAlertModalを一元管理
 */
export const RequireAuthProvider = ({children}: PropsWithChildren) => {
  const {status} = useAuth();
  const [opened, {open, close}] = useDisclosure(false);
  const queryClient = useQueryClient();
  const prevIsAuthenticatedRef = useRef<boolean | null>(null);

  const isActuallyAuthenticated = status === 'authenticated';
  // メンテナンスモード時は未ログインとして扱う
  const isAuthenticated = IS_MAINTENANCE_MODE ? false : isActuallyAuthenticated;
  // 認証状態が確定したかどうか（initializing/loading以外）
  const isAuthResolved = status !== 'initializing' && status !== 'loading';

  // isAuthenticatedがtrueからfalseに変わった時にキャッシュをクリア
  useEffect(() => {
    if (
      prevIsAuthenticatedRef.current === true &&
      isAuthenticated === false &&
      isAuthResolved
    ) {
      // ユーザー固有データを含むキャッシュをクリア
      queryClient.removeQueries({queryKey: ['entryTodos']});
      queryClient.removeQueries({queryKey: ['entryManagements']});
      queryClient.removeQueries({queryKey: ['entryCalendars']});
      queryClient.removeQueries({queryKey: ['entryManagementReminders']});
      queryClient.removeQueries({queryKey: ['progressMissions']});
      queryClient.removeQueries({queryKey: ['entryCalendarGoogleSyncStatus']});
    }
    prevIsAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated, isAuthResolved, queryClient]);

  const requireAuth = useCallback((): boolean => {
    if (isAuthenticated) {
      return true;
    }
    open();
    return false;
  }, [isAuthenticated, open]);

  const withRequireAuth = useCallback(
    <T extends (...args: any[]) => any>(
      action: T,
    ): ((...args: Parameters<T>) => ReturnType<T> | undefined) => {
      return (...args: Parameters<T>): ReturnType<T> | undefined => {
        if (!requireAuth()) {
          return undefined;
        }
        return action(...args);
      };
    },
    [requireAuth],
  );

  // メンテナンスモード時かつ実際にログイン中の場合、オーバーレイを表示
  const isMaintenanceMode = IS_MAINTENANCE_MODE && isActuallyAuthenticated;

  const value = useMemo(
    () => ({
      requireAuth,
      withRequireAuth,
      isAuthenticated,
      isAuthResolved,
      isMaintenanceMode,
    }),
    [
      requireAuth,
      withRequireAuth,
      isAuthenticated,
      isAuthResolved,
      isMaintenanceMode,
    ],
  );

  return (
    <RequireAuthContext.Provider value={value}>
      {children}
      <NoLoginAlertModal opened={opened} onClose={close} />
    </RequireAuthContext.Provider>
  );
};

/**
 * 認証が必要なアクションを管理するフック
 * EntryManagement配下のコンポーネントで使用
 */
export const useRequireAuth = (): RequireAuthContextType => {
  const context = useContext(RequireAuthContext);
  if (!context) {
    throw new Error('useRequireAuth must be used within RequireAuthProvider');
  }
  return context;
};
